import React, { useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import * as R from 'ramda';
import Snackbar from 'snackbar/Snackbar';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useCheckForUpdates } from 'hooks/useCheckForUpdates';

// utils
import { EvaluateFlag } from 'common/FeatureFlags/Flag';
import { useAppTitle, useUserRolePermissions } from './hooks';

// Components
import DrawerContainer from './common/drawer/Drawer.connector';
import { ErrorBoundary } from './common';
import ConfirmationDialog from './common/confirmation-dialog/ConfirmationDialog.connector';

// Contexts
import DrawerContextStore from './common/drawer/DrawerContext';
import { AuthContext } from './authentication/AuthContext';

const shouldLoadHeap = R.compose(env =>
  R.and(
    R.includes(env, ['production', 'staging', 'development', 'uat']),
    R.isNil(process.env.JEST_WORKER_ID)
  )
);

const shouldLoadDynatrace = R.compose(() => R.isNil(process.env.JEST_WORKER_ID));

const WALKME_SCRIPT_SRC = {
  beta: 'https://cdn.walkme.com/users/62fa0d69816248039a77ccd01d3c00a2/test/walkme_62fa0d69816248039a77ccd01d3c00a2_https.js',
  production:
    'https://cdn.walkme.com/users/62fa0d69816248039a77ccd01d3c00a2/walkme_62fa0d69816248039a77ccd01d3c00a2_https.js',
};

const shouldLoadWalkMe = () => {
  const env = process.env.REACT_APP_WALKME_ENVIRONMENT;
  return env === 'production' || env === 'beta';
};

// WalkMe reads window.walkme_variables when the snippet loads and again on each
// SPA page evaluation. Keep one object and update it in place so later routes
// and the snippet share the same reference. Assigning these inside walkme_ready
// is too late: WalkMe has already fallen back to its GUID identifier.
const syncWalkMeVariables = ({ affiliates, isSubscriber, userProfile }) => {
  const variables = {
    affiliates: affiliates || [],
    email: userProfile?.email || '',
    groups: userProfile?.groups || [],
    name: userProfile ? `${userProfile.given_name} ${userProfile.family_name}` : '',
    sub: userProfile?.sub || '',
    swiftPlus: isSubscriber || false,
  };

  if (!window.walkme_variables) {
    window.walkme_variables = variables;
    return;
  }

  Object.assign(window.walkme_variables, variables);
};

const App = () => {
  useCheckForUpdates();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { affiliates, currentAffiliate, setCurrentSelectedAffiliate, userProfile } =
    useContext(AuthContext);
  const heapID = process.env.REACT_APP_HEAP_ID;

  const container = useRef(null);
  const { isAdmin, isSubscriber } = useUserRolePermissions();
  const walkMeEnabled = EvaluateFlag('oneokSwiftWalkMe'); // hard-coded on purpose

  useEffect(() => {
    // have to split pathname because currentAffiliateId is not available on initial render
    // leave here in useEffect otherwise the security or other base routes won't route correctly
    const path = pathname.split('/');
    const urlAffiliate = path[2];
    const urlDataCompanyId = path[3];
    // if no url affiliate (customerIdName) then set one by pushing into url
    // else if current url affiliate !== currentAffiliates customerIdName then update
    // current affiliate to url affiliate
    // else there is no affiliate array or url affiliate & has security admin role direct to security
    if (currentAffiliate?.customerIdName && currentAffiliate?.dataCompanyId && !urlAffiliate) {
      navigate(`/affiliate/${currentAffiliate.customerIdName}/${currentAffiliate.dataCompanyId}`);
    } else if (
      currentAffiliate?.customerIdName &&
      urlAffiliate &&
      currentAffiliate?.customerIdName !== urlAffiliate &&
      currentAffiliate?.dataCompanyId !== urlDataCompanyId
    ) {
      const idx = affiliates.findIndex(
        ({ customerIdName, dataCompanyId }) =>
          customerIdName === urlAffiliate && dataCompanyId === parseInt(urlDataCompanyId, 10)
      );
      setCurrentSelectedAffiliate(idx);
    } else if (!currentAffiliate?.customerIdName && urlAffiliate && urlDataCompanyId) {
      const idx = affiliates.findIndex(
        ({ customerIdName, dataCompanyId }) =>
          customerIdName === urlAffiliate && dataCompanyId === parseInt(urlDataCompanyId, 10)
      );
      setCurrentSelectedAffiliate(idx);
    } else if (!currentAffiliate?.customerIdName && !urlAffiliate) {
      setCurrentSelectedAffiliate(0);
    } else if (
      currentAffiliate?.customerIdName &&
      urlAffiliate &&
      currentAffiliate?.customerIdName === urlAffiliate &&
      !urlDataCompanyId
    ) {
      navigate(`/affiliate/${currentAffiliate.customerIdName}/${currentAffiliate.dataCompanyId}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affiliates, currentAffiliate, isAdmin]);

  const title = useAppTitle();

  const ldClient = useLDClient();
  useEffect(() => {
    if (R.isNil(process.env.JEST_WORKER_ID)) {
      ldClient.identify({
        email: userProfile.email,
        key: userProfile.sub,
        name: userProfile.name,
      });
    }
  }, [ldClient, userProfile]);

  const walkMeIdentityReady = Boolean(userProfile?.sub || userProfile?.email);

  // Publish identifying variables before WalkMe loads, then refresh them on every
  // route so each page/flow has them when WalkMe evaluates segmentation.
  useLayoutEffect(() => {
    if (!walkMeEnabled || !shouldLoadWalkMe() || !walkMeIdentityReady) return;
    syncWalkMeVariables({ affiliates, isSubscriber, userProfile });
  }, [affiliates, isSubscriber, pathname, userProfile, walkMeEnabled, walkMeIdentityReady]);

  // Effect 1: Bootstrap Heap once — injects the loader script and calls heap.load/identify.
  useEffect(() => {
    if (
      !shouldLoadHeap(process.env.REACT_APP_BUILD_STATE) ||
      !heapID ||
      !userProfile ||
      window.__heapLoaded
    ) {
      return;
    }
    window.__heapLoaded = true;

    const script = document.createElement('script');
    script.id = 'heap-loader';
    script.type = 'text/javascript';
    script.text = `
      window.heapReadyCb=window.heapReadyCb||[],window.heap=window.heap||[],heap.load=function(e,t){window.heap.envId=e,window.heap.clientConfig=t=t||{},window.heap.clientConfig.shouldFetchServerConfig=!1;var a=document.createElement("script");a.type="text/javascript",a.async=!0,a.src="https://cdn.us.heap-api.com/config/"+e+"/heap_config.js";var r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(a,r);var n=["init","startTracking","stopTracking","track","resetIdentity","identify","getSessionId","getUserId","getIdentity","addUserProperties","addEventProperties","removeEventProperty","clearEventProperties","addAccountProperties","addAdapter","addTransformer","addTransformerFn","onReady","addPageviewProperties","removePageviewProperty","clearPageviewProperties","trackPageview"],i=function(e){return function(){var t=Array.prototype.slice.call(arguments,0);window.heapReadyCb.push({name:e,fn:function(){heap[e]&&heap[e].apply(heap,t)}})}};for(var p=0;p<n.length;p++)heap[n[p]]=i(n[p])};

      window.heap.load("${heapID}");
      window.heap.identify("${userProfile.email}");
    `;
    document.head.appendChild(script);
  }, [heapID, userProfile]);

  // Effect 2: Keep Heap user properties current whenever affiliate/user context changes.
  useEffect(() => {
    if (
      !shouldLoadHeap(process.env.REACT_APP_BUILD_STATE) ||
      !window.__heapLoaded ||
      !userProfile ||
      !currentAffiliate ||
      !affiliates
    ) {
      return;
    }
    window.heap.addUserProperties({ 'Current Affiliate': JSON.stringify(currentAffiliate) });
    window.heap.addUserProperties({ 'Unique Id': userProfile.sub });
    window.heap.addUserProperties({ Email: userProfile.email });
    window.heap.addUserProperties({
      'Global Company Id': currentAffiliate?.dataCompanyId,
    });
    window.heap.addUserProperties({
      Affiliates: JSON.stringify(affiliates.map(a => a.customerNameLong).join(', ')),
    });
    window.heap.addUserProperties({ 'Company Name': currentAffiliate?.customerNameLong });
    window.heap.addUserProperties({ Permissions: JSON.stringify(userProfile.groups.join(', ')) });
    window.heap.addUserProperties({ 'Swift Plus Enrollment': JSON.stringify(isSubscriber) });
    window.heap.addUserProperties({ 'Swift Version': process.env.REACT_APP_VERSION });
  }, [userProfile, currentAffiliate, affiliates, isSubscriber]);

  // WalkMe loader. Inject only after variables are on window, and only once.
  // walkme_ready is intentionally not used: it runs after WalkMe has already
  // chosen an identifier.
  useEffect(() => {
    if (!walkMeEnabled || !shouldLoadWalkMe() || !walkMeIdentityReady || window.__walkmeLoaded) {
      return;
    }

    const env = process.env.REACT_APP_WALKME_ENVIRONMENT;
    syncWalkMeVariables({ affiliates, isSubscriber, userProfile });
    window._walkmeConfig = { smartLoad: true };

    const walkme = document.createElement('script');
    walkme.type = 'text/javascript';
    walkme.async = true;
    walkme.src = WALKME_SCRIPT_SRC[env];
    window.__walkmeLoaded = true;
    document.head.appendChild(walkme);
  }, [affiliates, isSubscriber, userProfile, walkMeEnabled, walkMeIdentityReady]);

  return (
    <ErrorBoundary>
      <DrawerContextStore container={container}>
        <HelmetProvider>
          <Helmet>
            {process.env.REACT_APP_BUILD_STATE === 'production' && (
              <>
                {/* Global site tag (gtag.js) - Google Analytics */}
                <script async src="https://www.googletagmanager.com/gtag/js?id=UA-11018349-12" />
                <script>
                  {`
                      window.dataLayer = window.dataLayer || [];
                      function gtag(){dataLayer.push(arguments);}
                      gtag('js', new Date());
                      gtag('config', 'UA-11018349-12');
                    `}
                </script>
              </>
            )}
            {shouldLoadDynatrace() && (
              <>
                <script type="text/javascript">
                  {`
                    window.dynatrace={};
                    window.dynatrace.user = "${userProfile?.email || 'Anonymous'}";
                  `}
                </script>
              </>
            )}
            {/*
              This will be the title showing in the tab as the app is loading in
              otherwise it will show the next nested title in the tab
            */}
            <title>SWIFT - {title}</title>
          </Helmet>
          <Outlet />
        </HelmetProvider>
        <div data-testid="portal" ref={container} />
        <DrawerContainer />
        <ConfirmationDialog />
        <Snackbar />
      </DrawerContextStore>
    </ErrorBoundary>
  );
};

export default App;
